window.dataLayer = window.dataLayer || [];

// Toggled by the "Generic Event Logging" checkbox in the Magnolia site page properties.
// Wrapping push() logs every generic event, including the ones components push directly
// (accordion, filter tags, …) rather than through pushEvent().
if (window.app_config?.genericEventLoggingEnabled) {
    const originalPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = (...entries) => {
        entries.forEach((entry) => console.log('[dataLayer]', entry));
        return originalPush(...entries);
    };
}

const documentLanguage = document.documentElement.lang || '';

const selectorToElementName = [
    ['.ContactWizardCard', 'Contact Wizard'],
    ['.ProductTeaser--legalText', 'Product Teaser'],
    ['.LegalText', 'Legal Text'],
    ['.richtextWrapper', 'Richtext'],
    ['mb-rich-text', 'Richtext'],
];

function getElementName(anchor) {
    // Prefer explicit element name attribute
    const explicit = anchor.getAttribute('element') || '';
    if (explicit) return explicit;

    // If the anchor is in a shadow DOM, try to infer from the host element
    if (anchor.getRootNode() !== document) {
        const host = anchor.getRootNode().host;
        const hostElement = host.getAttribute('element') || '';
        if (hostElement) return hostElement;
    }

    // Infer from closest ancestor using the mapping
    for (const [selector, name] of selectorToElementName) {
        if (anchor.closest(selector)) return name;
    }
    return '';
}

let _header = null;
let _footer = null;
let _breadcrumb = null;

function getActionScope(el) {
    if (_header && _header.contains(el)) return 'header';
    if (_footer && _footer.contains(el)) return 'footer';
    if (_breadcrumb && _breadcrumb.contains(el)) return 'breadcrumb';
    return 'body';
}

function getNormalizedLabelForInternal(labelGermanUrl = '', href = '') {
    // Prefer label-german-url, then href
    let label = labelGermanUrl || href;

    // Normalize German language URLs if necessary
    if (!label) return label;

    if (documentLanguage === 'de' && label.includes('/de/')) {
        return label.replace('/de/', '/');
    }
    return label;
}

/**
 * Parses [data-tracking] and builds a payload.
 * Supports:
 *  - JSON in data-tracking (recommended)
 *  - Comma-separated string: "category,action,label,value"
 *  - Empty attribute => {}
 * Also supports "checkIfChecked" logic.
 */
function getCustomTrackingPayload(raw, checkedState, eventType = 'click') {
    const DEFAULT = { category: 'generic', action: 'body', label: undefined, value: undefined };

    // Normalize to an object: {}, parsed JSON, or CSV -> object
    let data = {};
    if (raw && raw.trim() !== '') {
        try {
            data = JSON.parse(raw);
        } catch {
            const parts = raw.split(',').map((s) => s.trim());
            const [category, action, label, value] = parts;
            data = { category, action, label, value };
        }
    }

    const payload = { ...DEFAULT, ...data };

    // Special: checkIfChecked
    if (data && data.checkIfChecked) {
        payload.label = checkedState ? 'active' : 'inactive';
    }

    if (data && !data.action) {
        payload.action = eventType || payload.action;
    }

    return payload;
}

function isCustomTrackingLink(raw) {
    return raw.trim() !== '';
}

function hasExtraTrackingData(raw) {
    return raw.trim() !== '';
}

function getExtraTrackingData(raw) {
    if (hasExtraTrackingData(raw)) {
        try {
            return JSON.parse(raw);
        } catch {
            return {};
        }
    }
    return {};
}

function isExternalLink({ href, hrefAttribute, isExternalLinkClass }) {
    if (isExternalLinkClass) return true;

    // /cx/i/e/l/otv is a different application that uses the same domain, so we need to track it as outbound
    if (hrefAttribute.includes('/cx/i/e/l/otv')) return true;
    if (
        !hrefAttribute ||
        hrefAttribute.startsWith('/') ||
        hrefAttribute.startsWith('#') ||
        hrefAttribute.startsWith('?')
    ) {
        return false;
    }

    try {
        const url = new URL(href || hrefAttribute, window.location.origin);
        return url.origin !== window.location.origin;
    } catch (_) {
        // If not a valid absolute URL, treat as internal unless it clearly starts with http
        return hrefAttribute.startsWith('http') || hrefAttribute.startsWith('//');
    }
}

function isDownloadLink({ hrefAttribute, isDownloadLinkClass }) {
    // Existing convention: /dam* or explicit .Download--link class
    return isDownloadLinkClass || hrefAttribute.includes('/dam/');
}

function isModalLink({ hrefAttribute, isModalLinkClass }) {
    // Existing convention: #* or explicit .Modal--link class
    // TODO: the # might not always be for modals... Links that open modals should have some aria attributes anyways.
    // TODO: lets implement those and then we can use them to find modal links
    return isModalLinkClass || hrefAttribute.startsWith('#');
}

function pushEvent({ event = 'generic', category, action, label, elementName }, extraData = {}) {
    try {
        window.dataLayer.push({ event, category, action, label, elementName, ...extraData });
    } catch (e) {
        // Optional: in non-production you can log a warning
        // console.warn('dataLayer push failed', e, { event, category, action, label, elementName });
    }
}

function getClickTrackingContext(anchor, event) {
    return {
        actionScope: getActionScope(anchor),
        checkedState: anchor.getAttribute('aria-checked') === 'true',
        elementName: getElementName(anchor),
        eventType: event.type,
        href: anchor.href || '',
        hrefAttribute: (anchor.getAttribute('href') || '').trim(),
        isDownloadLinkClass: anchor.classList.contains('Download--link'),
        isExternalLinkClass: anchor.classList.contains('External--link'),
        isModalLinkClass: anchor.classList.contains('Modal--link'),
        labelGermanUrl: anchor.getAttribute('label-german-url') || '',
        target: (anchor.getAttribute('target') || '').toLowerCase(),
        trackingExtraRaw: (anchor.getAttribute('data-tracking-extra') || '').trim(),
        trackingRaw: (anchor.getAttribute('data-tracking') || '').trim(),
        usesModifierClick: event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey,
    };
}

function shouldTrackSynchronously(context, event) {
    if (event.defaultPrevented || context.usesModifierClick) return false;
    if (context.target !== '' && context.target !== '_self') return false;
    if (!context.hrefAttribute || context.hrefAttribute.startsWith('javascript:')) return false;

    return !isModalLink(context);
}

function buildTrackingPayload(context) {
    if (isCustomTrackingLink(context.trackingRaw)) {
        return getCustomTrackingPayload(context.trackingRaw, context.checkedState, context.eventType);
    }

    if (isModalLink(context)) {
        return {
            category: 'interaction',
            action: context.actionScope,
            label: getNormalizedLabelForInternal(context.labelGermanUrl, context.hrefAttribute),
        };
    }

    if (isExternalLink(context)) {
        return {
            category: 'outbound links',
            action: 'click',
            label: context.hrefAttribute,
        };
    }

    if (isDownloadLink(context)) {
        const fileName = context.href.split('/').pop() || '';

        return {
            category: 'downloads',
            action: 'pdf',
            label: fileName,
        };
    }

    return {
        category: 'navigation',
        action: context.actionScope,
        label: getNormalizedLabelForInternal(context.labelGermanUrl, context.hrefAttribute),
    };
}

function trackClick(context) {
    const payload = buildTrackingPayload(context);
    if (!payload) return;

    pushEvent(
        {
            ...payload,
            elementName: context.elementName,
        },
        getExtraTrackingData(context.trackingExtraRaw),
    );
}

// Delegated link tracking
document.addEventListener('DOMContentLoaded', () => {
    _header = document.querySelector('header');
    _footer = document.querySelector('footer');
    _breadcrumb = document.querySelector('div.Breadcrumb');

    document.addEventListener('click', (event) => {
        const composedPath = event.composedPath?.() || [];
        const anchor = composedPath.find((element) => element instanceof Element && element.matches('a'));

        if (!anchor) return;

        const context = getClickTrackingContext(anchor, event);

        if (shouldTrackSynchronously(context, event)) {
            trackClick(context);
            return;
        }

        // For non-navigation clicks, defer parsing and dataLayer work after
        // capturing immutable click metadata.
        setTimeout(() => trackClick(context), 0);
    });
});

// Service modal toggle
document.addEventListener('DOMContentLoaded', () => {
    const serviceModalButton = document.querySelector('.ServiceModalWrapper');
    if (!serviceModalButton) return;

    serviceModalButton.addEventListener('click', (event) => {
        const el = event.currentTarget;
        const modal = document.querySelector('#serviceModal');
        const isOpen = modal && modal.getAttribute('style') === 'display: block;';
        const label = isOpen ? el.getAttribute('trackingLabelClose') : el.getAttribute('trackingLabelOpen');

        pushEvent({
            category: 'interaction',
            action: 'body',
            label,
            elementName: 'Contact-Popover',
        });
    });
});

// Landing page video: play tracking
document.addEventListener('DOMContentLoaded', () => {
    const playTriggers = document.querySelectorAll(
        '.HeroCampaign--cta button, .HeroCampaignVideo .HeroCampaignVideo--videoControl.playpause, .HeroCampaignHotspot',
    );

    playTriggers.forEach((btn) => {
        btn.addEventListener('click', (event) => {
            const current = event.currentTarget;
            const isPlayPause = current.classList.contains('playpause');
            const isPaused =
                isPlayPause &&
                current.parentElement &&
                current.parentElement.parentElement &&
                current.parentElement.parentElement.getAttribute('data-playback-state') === 'paused';

            if (!isPlayPause || (isPlayPause && isPaused)) {
                const videoLabel = current.getAttribute('data-video-label') || '';
                pushEvent({
                    category: 'landingpage',
                    action: 'video play',
                    label: videoLabel,
                });
            }
        });
    });
});

// Landing page video: progress tracking
document.addEventListener('DOMContentLoaded', () => {
    const videos = document.querySelectorAll('video.HeroCampaignVideo--video');

    // Track last fired threshold per video instance
    const lastThresholdMap = new WeakMap();

    function trackVideoProgress(percent, durationInSeconds) {
        pushEvent({
            category: 'landingpage',
            action: 'video progress',
            label: `${percent}% ${durationInSeconds}s`,
        });
    }

    function nextThreshold(prev) {
        if (prev < 25) return 25;
        if (prev < 50) return 50;
        if (prev < 75) return 75;
        if (prev < 100) return 100;
        return 100;
    }

    videos.forEach((video) => {
        lastThresholdMap.set(video, 0);
        video.addEventListener('timeupdate', () => {
            const duration = Math.max(1, Math.round(video.duration || 0));
            const currentTime = video.currentTime || 0;
            const percentPlayed = (currentTime / duration) * 100;

            let last = lastThresholdMap.get(video) || 0;
            let candidate = nextThreshold(last);

            while (percentPlayed >= candidate && last < candidate) {
                trackVideoProgress(candidate, duration);
                last = candidate;
                candidate = nextThreshold(last);
            }
            if (last !== lastThresholdMap.get(video)) {
                lastThresholdMap.set(video, last);
            }
        });
    });
});

// Campaign interactions
document.addEventListener('DOMContentLoaded', () => {
    const openFormTargets = document.querySelectorAll('.HeroCampaignQuestion--cta > *');
    openFormTargets.forEach((el) => {
        el.addEventListener('click', (evt) => {
            const nodeName = evt.currentTarget.nodeName.toLowerCase();
            const buttonTarget = nodeName === 'button' ? 'modal' : 'link';
            pushEvent({
                category: 'landingpage',
                action: 'form',
                label: `frage teilen - ${buttonTarget}`,
            });
        });
    });

    const campaignSubmitInputs = document.querySelectorAll('#campaign-modal #question');
    campaignSubmitInputs.forEach((el) => {
        el.addEventListener('change', () => {
            pushEvent({
                category: 'landingpage',
                action: 'form',
                label: 'frage stellen',
            });
        });
    });
});

// Tab tracking
// Tabs render as <mb-tab-link> custom elements (and a <select> on mobile), not as
// anchors, and tab-link stops click propagation — so the global link tracker never
// sees them. Listen for the bubbling, composed mb-tab-select event instead, which is
// dispatched by both the desktop tab links and the mobile select.
document.addEventListener('DOMContentLoaded', () => {
    const lastSelectedByGroup = new WeakMap();

    document.addEventListener('mb-tab-select', (event) => {
        const tabGroup = event.target?.closest?.('mb-tab-group');
        if (!tabGroup) return;

        const selected = event.selected;
        if (!selected) return;

        // A single mouse click dispatches both a focus and a click event on the tab
        // link, which would track the same tab twice — only track actual changes.
        if (lastSelectedByGroup.get(tabGroup) === selected) return;
        lastSelectedByGroup.set(tabGroup, selected);

        // The event carries the tab's human-readable label, falling back to the slug.
        const tabLabel = (event.label || selected).replace(/\s+/g, ' ').trim();

        // Prefix the tab group's name so multiple tab groups on a page can be told apart.
        const groupName = tabGroup.getAttribute('name');
        const label = groupName ? `${groupName} - ${tabLabel}` : tabLabel;

        pushEvent({
            category: 'interaction',
            action: getActionScope(tabGroup),
            label,
            elementName: 'tabs',
        });
    });
});

// Wizard step tracking
document.addEventListener('support-wizard-current-step', (event) => {
    pushEvent({
        event: 'wizard',
        category: 'wizard',
        action: event?.detail?.path || '-',
        label: '-',
    });
});
